import { Link, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { InfoCard, InfoCardRowDef, StaticMap } from '~components';
import { useAuthorize } from '~features/authentication';
import { DevicesService } from '~features/devices';
import { HubAutocomplete } from '~features/hub';
import { useSnackbar } from '~hooks';
import { GeoCoordinate } from '~interfaces';
import { buildGoogleMapsPlaceUrl } from '~utils';
import { toCityStreetString } from '~utils/addressUtils';

import BookingStatusEnum from '../../enums/bookingStatusEnum';
import BookingsService from '../../services/bookingsService';

interface UserGeneralInfoCardProps {
	bookingId: string;
	onEditClick?: () => void;
}

const bookingsService = new BookingsService();
const devicesService = new DevicesService();

const BookingLocationCard = ({ bookingId }: UserGeneralInfoCardProps) => {
	const { t } = useTranslation();
	const { authorizeForOrganisation } = useAuthorize();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();

	const {
		data: bookingData,
		isLoading: isBookingLoading,
		error: bookingError,
		mutate: mutateBooking,
	} = useSWR(
		[bookingsService.basePath, bookingId],
		([_, id]) => bookingsService.getBookingById(id),
		{
			shouldRetryOnError: false,
		}
	);

	const { data: deviceLocationdata, isLoading: isDeviceLocationLoading } = useSWR(
		bookingData?.item.device?.id != null ?
			[devicesService.basePath, bookingData.item.device.id, devicesService.locationSubPath]
		:	null,
		([_, id]) => devicesService.getDeviceLocation(id)
	);

	const { trigger } = useSWRMutation(
		[bookingsService.basePath, bookingId, bookingsService.hubSubPath],
		([_, id], { arg }) => bookingsService.changeBookingHub(id, arg),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(
					`${t('ui.success.message.updated')} ${t('views.addItem.settingsCard.locationCard.header')}`
				);
				mutateBooking();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const canChangeHubLocation = () =>
		authorizeForOrganisation(bookingData?.hub.organisation.id) &&
		bookingData?.status !== BookingStatusEnum.Completed &&
		bookingData?.status !== BookingStatusEnum.Cancelled;

	const rows: InfoCardRowDef[] | undefined =
		bookingData != null ?
			[
				{
					value: bookingData?.hub,
					renderRow: (value) => (
						<Stack spacing={1}>
							<Typography>
								{t('views.itemManagement.details.instanceDetails.locationInfo.assigned')}
							</Typography>
							<HubAutocomplete
								value={value}
								label=''
								organisationId={bookingData.hub.organisation.id}
								disabled={!canChangeHubLocation()}
								onChange={async (_, newValue) =>
									newValue != null ? await trigger(newValue.id) : null
								}
							/>
						</Stack>
					),
				},
				{
					headerName: t('ui.pickUpReturn'),
					value: bookingData?.hub?.address,
					valueFormatter: (value) => toCityStreetString(value),
				},
				{
					value: deviceLocationdata,
					renderRow: (value?: GeoCoordinate) => (
						<Stack spacing={1}>
							<Typography>
								{`${t('ui.label.device.name')} ${t('ui.label.location.labelName').toLowerCase()}`}
							</Typography>
							{!isDeviceLocationLoading ?
								<StaticMap isImage lat={value.latitude} lng={value.longitude} />
							:	<Skeleton variant='rectangular' height={200} />}
						</Stack>
					),
					hidden: deviceLocationdata == null,
				},
				{
					headerName: t('ui.label.gpsCoordinates'),
					value: deviceLocationdata,
					renderCell: (value) =>
						value != null ?
							<Link rel='noreferrer noopener' target='_blank' href={buildGoogleMapsPlaceUrl(value)}>
								{`${value.latitude}, ${value.longitude}`}
							</Link>
						:	'-',
					hidden: deviceLocationdata == null,
				},
			]
		:	undefined;

	return (
		<InfoCard
			title={`${t('ui.label.location')} ${t('ui.information.label')}`}
			version='legacy'
			rows={rows ?? []}
			loading={isBookingLoading}
			error={bookingError != null}
		/>
	);
};

export default BookingLocationCard;
